import { Radar } from "lucide-react";
import { useState } from "react";
import { Button, cx } from "../../../components/ui";
import { api } from "../../../lib/api";
import { useConnections } from "../../../lib/connections";

export interface LiveResult {
  cls: string;
  action: string;
  classFound: boolean;
  actionFound: boolean;
  actions?: string[];
  parameters: string[];
  fields: { name: string; type?: string }[];
  checkedAt: string;
  connection: string;
  unread?: string;
}

/**
 * The live check for an action Waypoint can't see (a tenant extension such as FUSD, CH, ZZ or BC; docs/phase3-plan.md
 * §3): Landmark's own metadata on one of your non-production connections. Only reads.
 */
export function LiveCheck({ product, cls, action, onChecked }: { product: "HCM" | "FSM"; cls: string; action: string; onChecked: (r: LiveResult, verifiedBy?: string) => void }) {
  const { apps } = useConnections();
  const options = (apps.ja?.options ?? []).filter((o) => o.usable && !o.production);
  const [connId, setConnId] = useState(() => (options.some((o) => o.connId === apps.ja?.active) ? apps.ja!.active! : (options[0]?.connId ?? "")));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LiveResult | null>(null);

  const check = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ check: LiveResult; verifiedBy?: string }>("/apps/ja/live-check", { body: { connId, product, cls, action } });
      setResult(r.check);
      onChecked(r.check, r.verifiedBy);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  // Close names from the class's own list, when the action wasn't there.
  const near = result && !result.actionFound ? (result.actions ?? []).filter((a) => a.toLowerCase().includes(action.toLowerCase().slice(0, 4))).slice(0, 8) : [];

  if (!options.length) return <p className="text-xs text-muted">To check it in Infor, you need a non-production Job Automation connection with your actor mapped.</p>;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-8 rounded-lg border border-line bg-surface px-2 text-sm" value={connId} onChange={(e) => setConnId(e.target.value)} aria-label="Connection to check on">
          {options.map((o) => (
            <option key={o.connId} value={o.connId}>
              {o.name} ({o.envLabel})
            </option>
          ))}
        </select>
        <Button onClick={() => void check()} busy={busy} disabled={!cls || !action} title="Reads Landmark's metadata for this class; nothing is changed">
          <Radar className="size-4" aria-hidden /> Check in Infor
        </Button>
      </div>
      {error && <p className="text-xs text-bad">{error}</p>}
      {result && (
        <div className={cx("rounded-lg p-2 text-xs", result.actionFound ? "bg-good-soft text-ink-2" : "bg-bad-soft text-ink-2")}>
          {!result.classFound
            ? `${cls} doesn't exist on ${result.connection}.`
            : result.actionFound
              ? `${cls}.${action} exists on ${result.connection}${result.parameters.length ? `; its parameters: ${result.parameters.join(", ")}` : "; it takes no declared parameters"}.`
              : `${cls} has no action ${action} on ${result.connection}.${near.length ? ` Did you mean ${near.join(", ")}?` : ""}`}
          {result.unread && <span className="block text-muted mt-1">Couldn't read the answer's shape: {result.unread}</span>}
        </div>
      )}
    </div>
  );
}
